// Brand fonts are free open-source fonts from Fontsource (almost all OFL), loaded from jsDelivr
// only in browsers of an agency that picked one. A saved font carries everything needed to load
// it (id, family, pinned version, weights), so the app never needs the catalog at runtime:
// public/brand-fonts.json (scripts/build-brand-fonts.mjs) only feeds the Branding page's picker.

export type FontCategory = "sans-serif" | "serif" | "display" | "handwriting" | "monospace";

/** As saved on the agency's branding (textFont / headingFont). */
export interface BrandFont {
  id: string;
  family: string;
  /** Exact npm version: pinned CSS, cached for a year. */
  v: string;
  /** The weights to load. */
  w: number[];
  c: FontCategory;
}

/** An entry in public/brand-fonts.json. */
export interface CatalogFont {
  i: string;
  f: string;
  v: string;
  c: FontCategory;
  w: number[];
}

/** text = everything; heading = the agency name at the top of the sidebar and page titles. */
export type BrandFontRole = "text" | "heading";

// Tailwind's base styles take <html>'s font from --default-font-family (system font when unset);
// globals.css gives h1/h2 --brand-heading-font, and so does the sidebar's agency name.
export const BRAND_FONT_VAR: Record<BrandFontRole, string> = {
  text: "--default-font-family",
  heading: "--brand-heading-font",
};

/** Body text gets only fonts that stay readable at small sizes. */
export const ROLE_CATEGORIES: Record<BrandFontRole, FontCategory[]> = {
  text: ["sans-serif", "serif", "monospace"],
  heading: ["sans-serif", "serif", "display", "handwriting", "monospace"],
};

const SYSTEM_SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FALLBACK: Record<FontCategory, string> = {
  "sans-serif": SYSTEM_SANS,
  serif: 'Georgia, "Times New Roman", serif',
  monospace: "ui-monospace, SFMono-Regular, Menlo, monospace",
  display: SYSTEM_SANS,
  handwriting: SYSTEM_SANS,
};

const CDN = "https://cdn.jsdelivr.net/npm/@fontsource/";
// Same checks as the API validator and scripts/build-brand-fonts.mjs.
const ID_RE = /^[a-z0-9-]{1,60}$/;
const VERSION_RE = /^\d+\.\d+\.\d+$/;
const FAMILY_RE = /^[A-Za-z0-9 .-]{1,80}$/;
const WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900];

/** Shape check for a font from the API or the localStorage cache; anything else is ignored. */
export function isBrandFont(x: unknown): x is BrandFont {
  if (!x || typeof x !== "object") return false;
  const f = x as Record<string, unknown>;
  return (
    typeof f.id === "string" &&
    ID_RE.test(f.id) &&
    typeof f.v === "string" &&
    VERSION_RE.test(f.v) &&
    typeof f.family === "string" &&
    FAMILY_RE.test(f.family) &&
    typeof f.c === "string" &&
    Object.hasOwn(FALLBACK, f.c) &&
    Array.isArray(f.w) &&
    f.w.length > 0 &&
    f.w.every((w) => WEIGHTS.includes(w as number))
  );
}

export const brandFontStack = (font: BrandFont) => `"${font.family}", ${FALLBACK[font.c]}`;

const cssUrl = (id: string, v: string, w: number) => `${CDN}${id}@${v}/${w}.css`;
export const brandFontCssUrls = (font: BrandFont) => font.w.map((w) => cssUrl(font.id, font.v, w));

/** One weight, for showing a font's name in itself in the picker. */
export const previewCssUrl = (f: CatalogFont) =>
  cssUrl(f.i, f.v, f.w.includes(400) ? 400 : f.w.reduce((a, b) => (Math.abs(b - 400) < Math.abs(a - 400) ? b : a)));

export const catalogFontStack = (f: CatalogFont) => `"${f.f}", ${FALLBACK[f.c]}`;

/** The weights a role uses where the font has them: regular to bold for text, heavier for titles. */
export function toBrandFont(f: CatalogFont, role: BrandFontRole): BrandFont {
  const want = role === "text" ? [400, 500, 600, 700] : [400, 600, 700, 800];
  let w = f.w.filter((x) => want.includes(x));
  if (!w.length) w = [f.w.reduce((a, b) => (Math.abs(b - 600) < Math.abs(a - 600) ? b : a))];
  return { id: f.i, family: f.f, v: f.v, w, c: f.c };
}

/** Adds a font stylesheet once. role marks the ones applyBrandFont manages. */
export function ensureFontCss(href: string, role?: BrandFontRole) {
  const attr = role ? `data-brand-font="${role}"` : "data-brand-font-preview";
  if (document.querySelector(`link[${attr}][href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  if (role) link.dataset.brandFont = role;
  else link.dataset.brandFontPreview = "";
  document.head.appendChild(link);
}

/** Applies a role's font, or clears it: its stylesheets plus the CSS variable. */
export function applyBrandFont(role: BrandFontRole, font: unknown) {
  const want = isBrandFont(font) ? brandFontCssUrls(font) : [];
  document.querySelectorAll<HTMLLinkElement>(`link[data-brand-font="${role}"]`).forEach((link) => {
    if (!want.includes(link.href)) link.remove();
  });
  want.forEach((href) => ensureFontCss(href, role));
  if (isBrandFont(font)) document.documentElement.style.setProperty(BRAND_FONT_VAR[role], brandFontStack(font));
  else document.documentElement.style.removeProperty(BRAND_FONT_VAR[role]);
}

/**
 * The pre-paint script in layout.tsx runs this with b = cached branding, d = document and
 * r = <html>'s style, so a brand font starts loading before first paint.
 */
export const PREPAINT_BRAND_FONTS_JS =
  `var FB=${JSON.stringify(FALLBACK)},FW=${JSON.stringify(WEIGHTS)};` +
  `[["textFont","text","${BRAND_FONT_VAR.text}"],["headingFont","heading","${BRAND_FONT_VAR.heading}"]].forEach(function(x){` +
  `var f=b[x[0]];if(!f||!${ID_RE}.test(f.id)||!${VERSION_RE}.test(f.v)||!${FAMILY_RE}.test(f.family)||!Object.prototype.hasOwnProperty.call(FB,f.c)||!Array.isArray(f.w))return;` +
  `f.w.forEach(function(w){if(FW.indexOf(w)<0)return;var l=d.createElement("link");l.rel="stylesheet";` +
  `l.href="${CDN}"+f.id+"@"+f.v+"/"+w+".css";l.setAttribute("data-brand-font",x[1]);d.head.appendChild(l);});` +
  `r.setProperty(x[2],'"'+f.family+'", '+FB[f.c]);});`;

let catalog: Promise<CatalogFont[]> | null = null;
/** The picker's full list (about 2,000 fonts, 25 KB gzipped). Fetched once, on the Branding page. */
export function loadFontCatalog() {
  catalog ??= fetch("/brand-fonts.json")
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`brand-fonts.json ${res.status}`))))
    .then((data: { fonts: CatalogFont[] }) => data.fonts)
    .catch((err: unknown) => {
      catalog = null;
      throw err;
    });
  return catalog;
}

// Shown before anyone searches: the picks from the font shortlist (open-source finds from
// GitHub first, then by style).
const GITHUB_FINDS = ["geist-sans", "open-sauce-sans", "uncut-sans", "metropolis", "cooper-hewitt", "apfel-grotezk"];
export const FEATURED: Record<BrandFontRole, string[]> = {
  heading: [
    ...GITHUB_FINDS, "redaction", "bagnard", "bluu-next", "chunk-five", "ostrich-sans", "norwester",
    "bricolage-grotesque", "unbounded", "syne", "space-grotesk", "mona-sans", "hubot-sans", "outfit", "sora",
    "instrument-sans", "familjen-grotesk", "red-hat-display", "lexend",
    "cinzel", "playfair-display", "cormorant-garamond", "dm-serif-display", "instrument-serif", "fraunces",
    "marcellus", "bodoni-moda", "young-serif", "gloock", "libre-caslon-display", "prata",
    "bebas-neue", "anton", "oswald", "big-shoulders-display", "archivo-black", "antonio", "league-gothic", "teko",
    "orbitron", "michroma", "audiowide", "chakra-petch", "russo-one", "exo-2", "saira", "rajdhani",
    "fredoka", "nunito", "quicksand", "comfortaa",
    "great-vibes", "dancing-script", "pacifico", "satisfy",
  ],
  text: [
    "inter", "roboto", "open-sans", "lato", "montserrat", "poppins", "manrope", "figtree", "dm-sans",
    "plus-jakarta-sans", "ibm-plex-sans", "public-sans", "atkinson-hyperlegible", "source-serif-4",
    "merriweather", "lora", ...GITHUB_FINDS,
  ],
};
